import type { Availability, Post, ProductVisibility, Role } from "../api/types";

/** The single simulated viewer for this prototype (see plan §4.1). */
export const VIEWER_USERNAME = "pixelryan";

export interface ProfileOverrides {
  displayName?: string;
  bio?: string;
  skills?: string[];
  location?: string;
  availability?: Availability;
  role?: Role;
}

interface ViewerState {
  followingCreators: Set<string>;
  likedPosts: Set<string>;
  bookmarkedPosts: Set<string>;
  joinedCommunities: Set<string>;
  cartProductIds: string[];
  productVisibility: Map<string, ProductVisibility>;
  /** Posts created by the viewer this session, newest first. */
  createdPosts: Post[];
  profileOverrides: ProfileOverrides;
  followerDeltaByUsername: Map<string, number>;
  memberDeltaBySlug: Map<string, number>;
  likeDeltaByPostId: Map<string, number>;
}

function createInitialState(): ViewerState {
  return {
    followingCreators: new Set(),
    likedPosts: new Set(),
    bookmarkedPosts: new Set(),
    joinedCommunities: new Set(),
    cartProductIds: [],
    productVisibility: new Map(),
    createdPosts: [],
    profileOverrides: {},
    followerDeltaByUsername: new Map(),
    memberDeltaBySlug: new Map(),
    likeDeltaByPostId: new Map(),
  };
}

let state = createInitialState();

/** Test/dev-only: returns the viewer state to a clean slate. */
export function resetViewerState(): void {
  state = createInitialState();
}

function bump(map: Map<string, number>, key: string, delta: number): void {
  map.set(key, (map.get(key) ?? 0) + delta);
}

// --- Follow (creator) ---------------------------------------------------

export function isFollowingCreator(username: string): boolean {
  return state.followingCreators.has(username);
}

export function setFollowingCreator(username: string, following: boolean): void {
  const wasFollowing = state.followingCreators.has(username);
  if (following) state.followingCreators.add(username);
  else state.followingCreators.delete(username);
  if (following !== wasFollowing) bump(state.followerDeltaByUsername, username, following ? 1 : -1);
}

export function followerDelta(username: string): number {
  return state.followerDeltaByUsername.get(username) ?? 0;
}

// --- Likes / bookmarks ------------------------------------------------------

export function isPostLiked(postId: string): boolean {
  return state.likedPosts.has(postId);
}

export function setPostLiked(postId: string, liked: boolean): void {
  const was = state.likedPosts.has(postId);
  if (liked) state.likedPosts.add(postId);
  else state.likedPosts.delete(postId);
  if (liked !== was) bump(state.likeDeltaByPostId, postId, liked ? 1 : -1);
}

export function likeDelta(postId: string): number {
  return state.likeDeltaByPostId.get(postId) ?? 0;
}

export function isPostBookmarked(postId: string): boolean {
  return state.bookmarkedPosts.has(postId);
}

export function setPostBookmarked(postId: string, bookmarked: boolean): void {
  if (bookmarked) state.bookmarkedPosts.add(postId);
  else state.bookmarkedPosts.delete(postId);
}

// --- Community membership ---------------------------------------------------

export function isCommunityJoined(slug: string): boolean {
  return state.joinedCommunities.has(slug);
}

export function setCommunityJoined(slug: string, joined: boolean): void {
  const was = state.joinedCommunities.has(slug);
  if (joined) state.joinedCommunities.add(slug);
  else state.joinedCommunities.delete(slug);
  if (joined !== was) bump(state.memberDeltaBySlug, slug, joined ? 1 : -1);
}

export function memberDelta(slug: string): number {
  return state.memberDeltaBySlug.get(slug) ?? 0;
}

// --- Cart -------------------------------------------------------------------

export function cartProductIds(): string[] {
  return [...state.cartProductIds];
}

export function isInCart(productId: string): boolean {
  return state.cartProductIds.includes(productId);
}

export function addToCart(productId: string): void {
  if (!state.cartProductIds.includes(productId)) state.cartProductIds.push(productId);
}

export function removeFromCart(productId: string): void {
  state.cartProductIds = state.cartProductIds.filter((id) => id !== productId);
}

// --- Created posts ------------------------------------------------------

export function addCreatedPost(post: Post): void {
  state.createdPosts.unshift(post);
}

export function createdPosts(): Post[] {
  return state.createdPosts;
}

// --- Dashboard product visibility ---------------------------------------

export function productVisibility(productId: string): ProductVisibility {
  return state.productVisibility.get(productId) ?? "visible";
}

export function setProductVisibility(productId: string, visibility: ProductVisibility): void {
  state.productVisibility.set(productId, visibility);
}

// --- Profile ----------------------------------------------------------------

export function profileOverrides(): ProfileOverrides {
  return state.profileOverrides;
}

export function updateProfileOverrides(overrides: ProfileOverrides): void {
  state.profileOverrides = { ...state.profileOverrides, ...overrides };
}
